// client/src/utils/imageHelper.js
// ================================================================
// Image URL Helper Utilities
// Handles Cloudinary URLs, local paths, and upload previews
// ================================================================

/**
 * Get correct image URL
 * Handles both Cloudinary URLs (full) and local paths (relative)
 * 
 * @param {string} imageUrl - Image URL from database
 * @returns {string|null} - Properly formatted URL or null
 * 
 * @example
 * // Cloudinary URL (already complete)
 * getImageUrl("https://res.cloudinary.com/.../image.png")
 * // Returns: "https://res.cloudinary.com/.../image.png"
 * 
 * // Local path (relative)
 * getImageUrl("uploads/products/image.jpg")
 * // Returns: "https://www.wholesalehub.gr/uploads/products/image.jpg"
 */
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Already a complete URL (Cloudinary or external)
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    // Apply Cloudinary auto-optimization (format + quality) for bandwidth savings
    if (imageUrl.includes('res.cloudinary.com') && !imageUrl.includes('f_auto')) {
      return imageUrl.replace('/upload/', '/upload/f_auto,q_auto/');
    }
    return imageUrl;
  }
  
  // Relative path - prepend server URL
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1';
  
  const SERVER_URL = isLocalhost 
    ? 'http://localhost:5000' 
    : window.location.origin; // Uses current domain
  
  // Remove leading slash if present to avoid double slashes
  const cleanPath = imageUrl.startsWith('/') ? imageUrl.substring(1) : imageUrl;
  
  return `${SERVER_URL}/${cleanPath}`;
};

/**
 * Create preview URL from File object
 * Returns data URL to avoid blob CSP issues
 * 
 * @param {File} file - File object from input
 * @returns {Promise<string>} - Data URL (base64)
 * 
 * @example
 * const file = e.target.files[0];
 * const preview = await createPreviewUrl(file);
 * setImagePreview(preview);
 * // preview = "data:image/png;base64,iVBORw0KG..."
 */
export const createPreviewUrl = (file) => {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No file provided'));
      return;
    }
    
    const reader = new FileReader();
    
    reader.onload = (e) => {
      resolve(e.target.result); // data:image/...;base64,...
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsDataURL(file);
  });
};

/**
 * Validate image file
 * Checks file type and size
 * 
 * @param {File} file - File to validate
 * @param {number} maxSizeMB - Maximum size in MB (default: 5)
 * @returns {Object} - { valid: boolean, error: string|null }
 * 
 * @example
 * const validation = validateImageFile(file, 5);
 * if (!validation.valid) {
 *   alert(validation.error);
 * }
 */
export const validateImageFile = (file, maxSizeMB = 5) => {
  if (!file) {
    return { valid: false, error: 'No file selected' };
  }
  
  // Check file type
  const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    return { 
      valid: false, 
      error: 'Invalid file type. Please upload JPEG, PNG, or WebP image.' 
    };
  }
  
  // Check file size
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return { 
      valid: false, 
      error: `File too large. Maximum size is ${maxSizeMB}MB.` 
    };
  }
  
  return { valid: true, error: null };
};

/**
 * Get image dimensions from File
 * Useful for validation or display
 * 
 * @param {File} file - Image file
 * @returns {Promise<Object>} - { width, height }
 * 
 * @example
 * const dimensions = await getImageDimensions(file);
 * console.log(`Image is ${dimensions.width}x${dimensions.height}`);
 */
export const getImageDimensions = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      URL.revokeObjectURL(url); // Clean up
      resolve({
        width: img.width,
        height: img.height
      });
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };
    
    img.src = url;
  });
};

/**
 * Compress image before upload
 * Reduces file size while maintaining quality
 * 
 * @param {File} file - Original image file
 * @param {number} maxWidth - Maximum width (default: 1920)
 * @param {number} quality - JPEG quality 0-1 (default: 0.8)
 * @returns {Promise<Blob>} - Compressed image blob
 * 
 * @example
 * const compressed = await compressImage(file, 1920, 0.8);
 * const newFile = new File([compressed], file.name, { type: 'image/jpeg' });
 */
export const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;
      
      // Resize if too large
      if (width > maxWidth) {
        height = (height * maxWidth) / width;
        width = maxWidth;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      
      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(url);
          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    };
    
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image for compression'));
    };
    
    img.src = url;
  });
};

export default {
  getImageUrl,
  createPreviewUrl,
  validateImageFile,
  getImageDimensions,
  compressImage
};